import React from 'react';
import {Autocomplete, Box, Button, Grid, Input, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography} from '@mui/material';
import {useDispatch, useSelector} from 'react-redux';
import {getSuppliers, selectSuppliersidname} from 'store/suppliers/suppliersSlice';
import {format} from 'date-fns';
import {enUS, he} from 'date-fns/locale';
import config from 'config';
import MyDatePicker from 'ui-component/MyDatePicker';
import JSDB, {NKTypeConvertor} from 'fromKotlin/nk';
import {dateObjectToString, roundDecimals} from 'fromKotlin/nkutils';
import {collect} from 'api/customer/documents.api';
import IItem from 'types/product_delivery';
import {HandLine} from 'types/DocumentsTypes/TaxNote';

export const isSupplierTaxDocument = (type?: string) =>
    type === 'sup_invoice' || type === 'sup_tax_invoice' || type === 'sup_refund';

type TotalsInput = {
    products?: IItem[];
    handArray?: HandLine[];
    deliveryCertificatesIds?: number[];
    hand?: number;
    discount?: number;
    date: Date;
    entId: string | number;
};

/** Recompute supplier invoice header numbers from the current lines. */
export function computeSupplierTaxTotals(input: TotalsInput): { preValue: number; taxValue: number; mainValue: number; discount: number } | null {
    const products = (input.products ?? []).filter((p) => p.quantity > 0 || p.quantityReturns > 0 || ((p.quantitySecond ?? 0) > 0 && (p.conversion_ratio ?? 0) > 0));
    const handArray = input.handArray ?? [];
    const deliveryIds = input.deliveryCertificatesIds ?? [];
    const hasHand = handArray.some((row) => roundDecimals(row.value * row.amount) !== 0) || Number(input.hand) > 0;
    if (products.length === 0 && !hasHand && deliveryIds.length === 0) return null;

    const date = dateObjectToString(input.date);
    const entId = Number(input.entId);
    let productsValue = 0;
    let productsTax = 0;
    if (products.length > 0) {
        const result = JSDB().productDeliveryToAmount(
            NKTypeConvertor().toKotlinList(collect(products, entId, false, false)),
            entId,
            date,
            false
        );
        productsTax = roundDecimals(result.second, 4);
        productsValue = roundDecimals(result.third, 4);
    }

    let includeDefault = false;
    try {
        includeDefault = JSDB().getSupplier(entId).first.getIncludeTax() == 1;
    } catch {
        includeDefault = false;
    }
    let handValue = 0;
    let handTax = 0;
    handArray.forEach((item) => {
        const include = item.includeTax !== undefined ? !!item.includeTax : includeDefault;
        const taxEach = item.withoutMaam ? 0 : (
            include
                ? item.value - item.value / JSDB().tax.get(date)
                : item.value * JSDB().tax.getPercentDivided(date)
        );
        const tax = taxEach * item.amount;
        handValue += item.value * item.amount + (include ? 0 : tax);
        handTax += tax;
    });

    let notesValue = 0;
    let notesTax = 0;
    let notesDiffValue = 0;
    let notesDiffTax = 0;
    if (deliveryIds.length > 0) {
        const result = JSDB().createTaxNoteDataFromNotesNew(entId, NKTypeConvertor().toKotlinList(deliveryIds), true, date);
        notesValue = Number(result?.first?.value || 0);
        notesTax = Number(result?.third?.second || 0);
        notesDiffValue = Number(result?.third?.third?.value || 0);
        notesDiffTax = Number(result?.third?.third?.taxPaid || 0);
    }

    const totalValue = productsValue + handValue + notesValue + notesDiffValue;
    const totalTax = productsTax + handTax + notesTax + notesDiffTax;
    const totalBefore = roundDecimals(totalValue - totalTax, 4);
    const discount = Number(input.discount ?? 0);
    const taxAfter = totalTax * (1 - discount / 100);
    const beforeAfter = totalBefore * (1 - discount / 100);
    return {
        preValue: totalBefore,
        taxValue: roundDecimals(taxAfter, 4),
        mainValue: roundDecimals(taxAfter + beforeAfter, 4),
        discount
    };
}

export function useSupplierTaxLineSync({
    enabled,
    products,
    handArray,
    date,
    entId,
    discount,
    deliveryCertificatesIds,
    hand,
    setInternalEdit
}: {
    enabled: boolean;
    products?: IItem[];
    handArray?: HandLine[];
    date: Date;
    entId: string | number;
    discount?: number;
    deliveryCertificatesIds?: number[];
    hand?: number;
    setInternalEdit?: (edit: any) => void;
}) {
    const skip = React.useRef(true);
    const extras = React.useRef({date, discount, deliveryCertificatesIds, hand, entId});
    extras.current = {date, discount, deliveryCertificatesIds, hand, entId};
    React.useEffect(() => {
        if (enabled) skip.current = true;
    }, [enabled]);
    const handKey = JSON.stringify(handArray ?? []);
    React.useEffect(() => {
        if (!enabled || !setInternalEdit) return;
        if (skip.current) {
            skip.current = false;
            return;
        }
        const totals = computeSupplierTaxTotals({
            products,
            handArray,
            date: extras.current.date,
            entId: extras.current.entId,
            discount: extras.current.discount,
            deliveryCertificatesIds: extras.current.deliveryCertificatesIds,
            hand: extras.current.hand
        });
        setInternalEdit((prev: any) => ({
            ...prev,
            ...(totals || {}),
            lines_edit: 1
        }));
    }, [enabled, products, handKey, setInternalEdit]);
}

export function SupplierEntityPicker({
    entId,
    entName,
    onChange
}: {
    entId?: string | number;
    entName?: string;
    onChange: (id: string, name: string) => void;
}) {
    const dispatch = useDispatch();
    const options = useSelector(selectSuppliersidname) as {id: string | number; name: string}[];
    React.useEffect(() => {
        if (!options?.length) dispatch(getSuppliers() as any);
    }, [dispatch, options?.length]);
    const current = options.find((option) => String(option.id) === String(entId))
        ?? (entId ? {id: entId, name: entName || String(entId)} : null);
    return (
        <Autocomplete
            size='small'
            options={options}
            value={current}
            isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
            getOptionLabel={(option) => option?.name ? `${option.name} (${option.id})` : ''}
            onChange={(_, value) => {
                if (!value || String(value.id) === String(entId)) return;
                onChange(String(value.id), value.name);
            }}
            renderInput={(params) => <TextField {...params} label='ספק' size='small'/>}
        />
    );
}

export function SupplierDocumentDate({
    editing,
    date,
    onChange
}: {
    editing: boolean;
    date: Date;
    onChange: (date: Date) => void;
}) {
    if (!editing) {
        return (
            <Typography variant='body2'>
                {format(date, 'PPP', {locale: config.i18n === 'he' ? he : enUS})}
            </Typography>
        );
    }
    return (
        <MyDatePicker
            date={date}
            autoFocus
            setDate={(next) => next && onChange(next)}
            isLoading={undefined}
        />
    );
}

export function applySupplierMoneyEdit(internalEdit: any, document: any, field: 'pre' | 'tax' | 'total' | 'discount' | 'left', raw: string, setInternalEdit?: (edit: any) => void) {
    if (!setInternalEdit) return;
    const base = {...document, ...internalEdit};
    const pre = Number(base.preValue ?? 0);
    const tax = Number(base.taxValue ?? 0);
    const discount = Number(base.discount ?? 0);
    const num = Number(raw);
    const next: any = {};
    if (field === 'pre') {
        next.preValue = num;
        next.taxValue = tax;
        next.discount = discount;
        next.mainValue = roundDecimals(num * (1 - discount / 100) + tax, 4);
    } else if (field === 'tax') {
        next.preValue = pre;
        next.taxValue = num;
        next.discount = discount;
        next.mainValue = roundDecimals(pre * (1 - discount / 100) + num, 4);
    } else if (field === 'discount') {
        next.preValue = pre;
        next.taxValue = tax;
        next.discount = num;
        next.mainValue = roundDecimals(pre * (1 - num / 100) + tax, 4);
    } else if (field === 'total') {
        next.preValue = pre;
        next.discount = discount;
        next.mainValue = num;
        next.taxValue = roundDecimals(num - pre * (1 - discount / 100), 4);
    } else {
        next.value_left = num;
        next.value_left_edit = 1;
    }
    if (field !== 'left' && !internalEdit?.value_left_edit && document.type !== 'sup_tax_invoice') {
        const paid = Number(document.mainValue || 0) - Number(document.value_left || 0);
        next.value_left = Math.max(0, roundDecimals(next.mainValue - paid, 4));
    }
    setInternalEdit({...internalEdit, ...next});
}

export function SupplierMoneyInput({value, onChange}: {value: number; onChange: (raw: string) => void}) {
    return (
        <Input
            type='number'
            value={Number.isFinite(value) ? value : 0}
            onChange={(e) => onChange(e.target.value)}
            inputProps={{style: {direction: 'ltr', textAlign: 'left'}}}
        />
    );
}

const emptyHand = (): HandLine => ({description: '', amount: 1, value: 0, tax: 0});

export function SupplierHandLines({
    rows,
    onChange
}: {
    rows: HandLine[];
    onChange: (rows: HandLine[]) => void;
}) {
    const update = (index: number, patch: Partial<HandLine>) => {
        onChange(rows.map((row, i) => (i === index ? {...row, ...patch} : row)));
    };
    return (
        <Box sx={{mb: 2}}>
            <Typography variant='subtitle1' fontWeight='bold' sx={{mb: 1}}>שורות ידניות</Typography>
            <Table size='small'>
                <TableHead>
                    <TableRow>
                        <TableCell align='right'>תיאור</TableCell>
                        <TableCell align='right'>כמות</TableCell>
                        <TableCell align='right'>מחיר</TableCell>
                        <TableCell align='right'>סהכ</TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, index) => (
                        <TableRow key={index}>
                            <TableCell>
                                <Input value={row.description} onChange={(e) => update(index, {description: e.target.value})} />
                            </TableCell>
                            <TableCell>
                                <Input type='number' value={row.amount} onChange={(e) => update(index, {amount: Number(e.target.value)})} />
                            </TableCell>
                            <TableCell>
                                <Input type='number' value={row.value} onChange={(e) => update(index, {value: Number(e.target.value)})} />
                            </TableCell>
                            <TableCell>{roundDecimals(row.value * row.amount, 2)}</TableCell>
                            <TableCell>
                                <Button size='small' onClick={() => onChange(rows.filter((_, i) => i !== index))}>הסר</Button>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <Button size='small' sx={{mt: 1}} onClick={() => onChange(rows.concat(emptyHand()))}>הוסף שורה ידנית</Button>
        </Box>
    );
}

export function SupplierTotalsDiscountLabel({editing, discount, onChange}: {editing: boolean; discount: number; onChange: (raw: string) => void}) {
    if (!editing) {
        return (
            <Typography variant='subtitle1' fontWeight='bold'>
                {(discount > 0) ? `הנחה ${discount}%` : `תוספת ${Math.abs(discount)}%`}
            </Typography>
        );
    }
    return (
        <Grid container alignItems='center' spacing={1}>
            <Grid item>
                <Typography variant='subtitle1' fontWeight='bold'>הנחה %</Typography>
            </Grid>
            <Grid item>
                <SupplierMoneyInput value={discount} onChange={onChange} />
            </Grid>
        </Grid>
    );
}
